import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiInstance } from '../services/service-trips-api/service-trips-api';
import {
  GetTripResponse,
  GetTripsPayload,
  UpdateTripRequest,
  toTripId,
} from '../services/service-trips-api/types';
import { sortTripGroupsByPickupTime } from '../utils/trips';
import { QueryKeys } from './types';

export const useUpdateTripMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Trip] });
    },
    mutationFn: async ({
      tripId,
      payload,
    }: {
      tripId: number;
      payload: UpdateTripRequest;
    }) => await apiInstance.updateTrip(toTripId(tripId), payload),
  });
};

const prepareTripSearchPayload = (
  searchValue: GetTripsPayload
): GetTripsPayload => {
  const searchItemExists = searchValue?.searchItem;
  const searchItemIsNotEmpty =
    searchItemExists !== undefined && searchItemExists.length > 0;
  const isNumber =
    searchItemIsNotEmpty && !isNaN(Number(searchValue.searchItem));

  return {
    personId: isNumber ? Number.parseInt(searchValue.searchItem as string) : 0,
    name: isNumber ? '' : searchValue.searchItem,
    address: searchValue.address,
    fromDate: searchValue.fromDate,
    toDate: searchValue.toDate,
    grouping: searchValue.grouping,
    itemsPerPage: searchValue.itemsPerPage,
    index: searchValue.index,
    sortField: searchValue.sortField,
    sortOrder: searchValue.sortOrder,
  };
};

export const useTripsDetailQuery = (bookingId: number) =>
  useQuery({
    queryKey: [QueryKeys.Trip, QueryKeys.TripDetails, bookingId],
    queryFn: () => apiInstance.getTrip(toTripId(bookingId)),
  });

export const useTripListQuery = (
  searchTerms: GetTripsPayload = {},
  grouping?: string,
  enabled = true
) =>
  useQuery({
    queryKey: [QueryKeys.Trip, QueryKeys.ListTrips, searchTerms],
    queryFn: async () => {
      const payload = prepareTripSearchPayload(searchTerms);
      const response = await apiInstance.getTripsList(payload);
      return response;
    },
    select: (data) => {
      const groupedItems =
        data.items &&
        [
          ...data.items.reduce(
            (
              memo: Map<number, ReadonlyArray<GetTripResponse>>,
              item: GetTripResponse
            ) => {
              let hash;
              if (grouping !== 'soloTrip' && item.coordinatedBookRefnr) {
                hash = item.coordinatedBookRefnr;
              } else {
                hash = item.id ?? 0;
              }

              const previousValue = memo.get(hash);
              if (previousValue) {
                memo.set(hash, [...previousValue, item]);
              } else {
                memo.set(hash, [item]);
              }

              return memo;
            },
            new Map<number, ReadonlyArray<GetTripResponse>>()
          ),
        ].map(([k, v]) => ({ coordinatedBookRefNr: k, items: v || [] }));
      const items = groupedItems
        ? sortTripGroupsByPickupTime(groupedItems)
        : groupedItems;
      return { items, count: data?.count };
    },
    enabled,
  });
